import { setAccessToken } from '@/lib/api/client';
import { getMeApi, logoutApi, refreshTokenApi, stopImpersonationApi } from '@/lib/auth/authApi';
import { type AuthUser, Role } from '@shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  /** true une fois que le check initial de session est terminé (refresh ou restore) */
  sessionChecked: boolean;
  /** true quand un SUPER_ADMIN impersonne ce compte */
  isImpersonating: boolean;

  login: (user: AuthUser, accessToken: string) => void;
  logout: () => void;
  hasRole: (roles: Role | Role[]) => boolean;
  updateUser: (user: Partial<AuthUser>) => void;
  markSessionChecked: () => void;
  refreshSession: () => Promise<void>;
  tryRestoreSession: () => Promise<void>;
  startImpersonation: (user: AuthUser, accessToken: string) => void;
  stopImpersonation: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      sessionChecked: false,
      isImpersonating: false,

      login: (user, accessToken) => {
        setAccessToken(accessToken);
        set({ user, accessToken, isAuthenticated: true, isImpersonating: false });
      },

      markSessionChecked: () => set({ sessionChecked: true }),

      logout: () => {
        // Invalider le refresh token côté serveur pour couper le SSO cross-app
        if (get().isImpersonating) stopImpersonationApi().catch(() => {});
        else logoutApi().catch(() => {});
        setAccessToken(null);
        set({ user: null, accessToken: null, isAuthenticated: false, isImpersonating: false });
      },

      hasRole: roles => {
        const { user } = get();
        if (!user) return false;
        const allowed = Array.isArray(roles) ? roles : [roles];
        return allowed.includes(user.role);
      },

      updateUser: partial => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...partial } });
        }
      },

      refreshSession: async () => {
        if (!get().isAuthenticated) return;
        try {
          const user = await getMeApi();
          set({ user });
        } catch {
          get().logout();
        }
      },

      startImpersonation: (user, accessToken) => {
        setAccessToken(accessToken);
        set({ user, accessToken, isAuthenticated: true, isImpersonating: true, sessionChecked: true });
      },

      stopImpersonation: async () => {
        try {
          await stopImpersonationApi();
          // Retourner sur l'admin après stop
          const adminUrl =
            (import.meta.env.VITE_URL_ADMIN as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:5231';
          setAccessToken(null);
          set({ user: null, accessToken: null, isAuthenticated: false, isImpersonating: false });
          window.location.href = adminUrl + '/admin/users';
        } catch {
          // Token expiré ou erreur réseau — nettoyer l'état et retourner à l'admin
          const adminUrl =
            (import.meta.env.VITE_URL_ADMIN as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:5231';
          setAccessToken(null);
          set({ user: null, accessToken: null, isAuthenticated: false, isImpersonating: false });
          window.location.href = adminUrl + '/admin/users';
        }
      },

      // Tente de restaurer la session depuis le cookie refresh token
      // (utile quand l'utilisateur s'est connecté depuis le backoffice admin)
      tryRestoreSession: async () => {
        if (get().isImpersonating) {
          set({ sessionChecked: true });
          return;
        }
        try {
          const { accessToken: newToken } = await refreshTokenApi();
          setAccessToken(newToken);
          const user = await getMeApi();
          get().login(user, newToken);
        } catch {
          // Aucun cookie valide — rester déconnecté silencieusement
        } finally {
          set({ sessionChecked: true });
        }
      },
    }),
    {
      name: 'bc_front_auth',
      partialize: state => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
        isImpersonating: state.isImpersonating,
      }),
      onRehydrateStorage: () => state => {
        if (state?.accessToken) {
          setAccessToken(state.accessToken);
        }
      },
    },
  ),
);

if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    useAuthStore.getState().logout();
  });
}
